import { Box, CircularProgress, Snackbar, Stack, Table, TableBody, TableCell, TableRow, TextField, Tooltip } from '@mui/material'
import { createFilterOptions } from '@mui/material/Autocomplete'
import { keyframes, styled } from '@mui/material/styles'
import { Alert, InfoBox, tokens } from 'ComponentLibrary'
import { ContentChip } from 'ComponentLibrary/src/piazza/content'
import { IconButton, Typography } from 'ComponentLibrary/src/piazza'
import {
    CalculatorIcon,
    CheckCircleIcon,
    CopyIcon,
    DownloadSimpleIcon,
    PlusIcon,
    SparkleIcon,
    WarningIcon,
    XCircleIcon,
    XIcon,
} from '@phosphor-icons/react'
import { ChangeEvent, FocusEvent, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'

import { HeaderBar, KpiTableContainer } from 'ComponentLibrary/src/playground/tools/ValXmlExtractor/components/styled'
import type { DisplayRow, KpiRow, NewKpiDraft, RentalSection, ValXmlOption } from '../types'
import { TextButton } from 'ComponentLibrary/src/piazza/buttons/TextButton/TextButton'
import { AddKpiModal } from './AddKpiModal'
import {
    canonicalizeValXmlPath,
    normaliseValXmlPathForGrouping,
} from 'ComponentLibrary/src/playground/tools/ValXmlExtractor/utils/valxml-builder'
import type { GeneralSectionLabel } from 'ComponentLibrary/src/playground/tools/ValXmlExtractor/utils/prompt-reference'
import {
    classifyGeneralSection,
    GENERAL_SECTION_ORDER,
    RENTAL_SITUATION_GROUPS,
} from 'ComponentLibrary/src/playground/tools/ValXmlExtractor/utils/prompt-reference'
import { VALXML_ATTRIBUTE_DEFINITIONS } from 'ComponentLibrary/src/playground/tools/ValXmlExtractor/valxml-schema'
import { FORCED_DEFAULT_VALXML_FIELDS, MANUAL_ENTRY_CONFIDENCE } from '../constants'
import {
    formatDisplayValue,
    formatUnit,
    getConfidenceStyle,
    getKpiRowStyles,
    type KpiValueFormat,
    normaliseInputValue,
} from 'ComponentLibrary/src/playground/tools/ValXmlExtractor/utils/response-processor'
import { getAcceptedReviewProposalUpdates } from 'ComponentLibrary/src/playground/tools/ValXmlExtractor/utils/secondary-check'
import {
    DEFAULT_ADD_KPI_MODAL_COPY,
    DEFAULT_VAL_XML_FIELD_DISPLAY_COPY,
    type AddKpiModalCopy,
    type ValXmlFieldDisplayCopy,
} from './AddKpiModal'

export type KpiSectionCopy = {
    addModal: AddKpiModalCopy
    valXmlFields: ValXmlFieldDisplayCopy
    validation: {
        missingValXmlField: string
        missingValue: string
        kpiAdded: string
    }
    labels: {
        review: string
        page: string
        confidence: string
        manualConfidence: string
        valXmlPath: string
        explanation: string
        notes: string
        codeInterpreterUsed: string
        title: string
        valXml: string
    }
    actions: {
        removeKpi: string
        copyValue: string
        acceptProposal: string
        rejectProposal: string
        addKpi: string
        addMissingKpi: string
        reviewTooltip: string
        reviewRunning: string
        review: string
    }
    placeholders: {
        value: string
    }
    feedback: {
        valueCopied: string
        valXmlUnavailable: string
        valXmlExportStarted: string
        extractingInitial: string
        extractingMore: string
    }
    booleanLabels: {
        true: string
        false: string
    }
    generalSectionLabel: (label: string) => string
    rentalSectionLabel: (label: string) => string
}

export const DEFAULT_KPI_SECTION_COPY: KpiSectionCopy = {
    addModal: DEFAULT_ADD_KPI_MODAL_COPY,
    valXmlFields: DEFAULT_VAL_XML_FIELD_DISPLAY_COPY,
    validation: {
        missingValXmlField: 'Bitte ein ValXML-Feld wählen',
        missingValue: 'Bitte einen Wert angeben',
        kpiAdded: 'KPI hinzugefügt',
    },
    labels: {
        review: 'Überprüfung',
        page: 'Seite',
        confidence: 'Confidence',
        manualConfidence: 'Manuell',
        valXmlPath: 'ValXML Pfad',
        explanation: 'Erklärung',
        notes: 'Notizen',
        codeInterpreterUsed: 'Code Interpreter verwendet',
        title: 'Kennzahlen',
        valXml: 'ValXML',
    },
    actions: {
        removeKpi: 'Kennzahl entfernen',
        copyValue: 'Wert kopieren',
        acceptProposal: 'Vorschlag übernehmen',
        rejectProposal: 'Vorschlag verwerfen',
        addKpi: 'Kennzahl hinzufügen',
        addMissingKpi: 'Fehlende Kennzahl hinzufügen',
        reviewTooltip: 'Alle Werte nochmals mittels KI überprüfen',
        reviewRunning: 'Prüfung läuft',
        review: 'Überprüfen',
    },
    placeholders: {
        value: 'Wert eingeben',
    },
    feedback: {
        valueCopied: 'Wert wurde in die Zwischenablage kopiert',
        valXmlUnavailable: 'ValXML steht noch nicht zur Verfügung',
        valXmlExportStarted: 'ValXML-Export gestartet',
        extractingInitial: 'Kennzahlen werden extrahiert',
        extractingMore: 'Weitere Kennzahlen werden extrahiert',
    },
    booleanLabels: {
        true: 'Ja',
        false: 'Nein',
    },
    generalSectionLabel: (label) => label,
    rentalSectionLabel: (label) => label,
}

const RENTAL_GROUP_METADATA = RENTAL_SITUATION_GROUPS.map((group) => ({
    label: group.label,
    pathSet: new Set(group.paths.map((path) => normaliseValXmlPathForGrouping(path) ?? path)),
}))

const FORCED_DEFAULT_PATH_SET = new Set(FORCED_DEFAULT_VALXML_FIELDS.map((path) => normaliseValXmlPathForGrouping(path) ?? path))
const DECIMAL_BASE_TYPES = new Set(['xs:decimal', 'xs:double', 'xs:float'])
const DATE_BASE_TYPES = new Set(['xs:date'])
const INTEGER_BASE_TYPES = new Set([
    'xs:int',
    'xs:integer',
    'xs:long',
    'xs:short',
    'xs:byte',
    'xs:nonnegativeinteger',
    'xs:nonNegativeInteger',
    'xs:positiveinteger',
    'xs:positiveInteger',
    'xs:unsignedint',
    'xs:unsignedInt',
    'xs:unsignedlong',
    'xs:unsignedLong',
    'xs:unsignedshort',
    'xs:unsignedShort',
    'xs:unsignedbyte',
    'xs:unsignedByte',
])

const normalizeDefinitionPathKey = (path?: string | null): string | undefined => normaliseValXmlPathForGrouping(path)?.toLowerCase()

const ATTRIBUTE_DEFINITION_BY_PATH = new Map(
    VALXML_ATTRIBUTE_DEFINITIONS.flatMap((definition) => {
        const key = normalizeDefinitionPathKey(definition.path)
        return key ? [[key, definition] as const] : []
    })
)

const getValueFormatForPath = (path?: string | null): KpiValueFormat => {
    const definition = ATTRIBUTE_DEFINITION_BY_PATH.get(normalizeDefinitionPathKey(path) ?? '')
    const baseType = (definition?.baseType ?? definition?.simpleType?.base ?? '').trim()
    if (DATE_BASE_TYPES.has(baseType)) {
        return 'date'
    }
    if (DECIMAL_BASE_TYPES.has(baseType)) {
        return 'decimal'
    }
    if (INTEGER_BASE_TYPES.has(baseType) || baseType.toLowerCase().endsWith('integer')) {
        return 'integer'
    }
    return 'text'
}

const isYearLikeIntegerPath = (path?: string | null): boolean => {
    const normalizedPath = normalizeDefinitionPathKey(path) ?? ''
    return normalizedPath.includes('yearof') || normalizedPath.includes('yearnumber') || normalizedPath.includes('constructionyear')
}

type GeneralSection = {
    label: GeneralSectionLabel
    rows: DisplayRow[]
}

const ellipsisAnimation = keyframes`
    0% { content: '.'; }
    33% { content: '..'; }
    66% { content: '...'; }
    100% { content: '...'; }
`

const PendingMessage = styled(Typography)(() => ({
    '&::after': {
        content: '""',
        display: 'inline-block',
        minWidth: '1.5em',
        textAlign: 'left',
        marginLeft: '4px',
        animation: `${ellipsisAnimation} 1.2s steps(1, end) infinite`,
    },
}))

type KpiSectionProps = {
    isPending: boolean
    kpiRows: KpiRow[]
    onRowClick?: (row: KpiRow) => void
    onDownloadValXml?: () => void
    canDownloadValXml?: boolean
    onRunReview: () => void
    isReviewPending: boolean
    canRunReview: boolean
    onUpdateRow: (index: number, updates: Partial<KpiRow>) => void
    onAddRow: (row: KpiRow) => void
    onRemoveRow: (index: number) => void
    lastReviewSummary: string | null
    onReviewSummaryShown: () => void
    locale?: string
    copy?: KpiSectionCopy
}

export const KpiSection = ({
    isPending,
    kpiRows,
    onRowClick,
    onDownloadValXml,
    canDownloadValXml,
    onRunReview,
    isReviewPending,
    canRunReview,
    onUpdateRow,
    onAddRow,
    onRemoveRow,
    lastReviewSummary,
    onReviewSummaryShown,
    locale = 'de-DE',
    copy = DEFAULT_KPI_SECTION_COPY,
}: KpiSectionProps) => {
    const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'info' | 'error' } | null>(null)
    const [newKpiDraft, setNewKpiDraft] = useState<NewKpiDraft | null>(null)
    const [pendingValueEdits, setPendingValueEdits] = useState<Record<string, string>>({})
    const isAddModalOpen = Boolean(newKpiDraft)
    useEffect(() => {
        if (lastReviewSummary) {
            setSnackbar({ message: lastReviewSummary, severity: 'success' })
            onReviewSummaryShown()
        }
    }, [lastReviewSummary, onReviewSummaryShown])

    const getRowEditKey = useCallback((row: DisplayRow): string => {
        const trimmedPath = row.valXmlPath?.trim().toLowerCase()
        if (trimmedPath) {
            return `path::${trimmedPath}`
        }
        const normalizedName = row.name.trim().toLowerCase()
        const normalizedUnit = row.unit ? row.unit.trim().toLowerCase() : ''
        return `name::${normalizedName}::${normalizedUnit}::${row.originalIndex}`
    }, [])

    const valXmlOptions = useMemo<ValXmlOption[]>(() => {
        const parentPaths = new Set<string>()
        VALXML_ATTRIBUTE_DEFINITIONS.forEach((definition) => {
            const lastDot = definition.path.lastIndexOf('.')
            if (lastDot > 0) {
                parentPaths.add(definition.path.slice(0, lastDot))
            }
        })
        return VALXML_ATTRIBUTE_DEFINITIONS.filter((definition) => !parentPaths.has(definition.path))
            .map((definition) => {
                const hasPrefix = definition.path.toLowerCase().startsWith('valxml')
                const canonicalPath = hasPrefix ? definition.path.replace(/^valxml/i, 'ValXML') : `ValXML.${definition.path}`
                return {
                    path: canonicalPath,
                    definition,
                }
            })
            .sort((a, b) => {
                const labelA = copy.valXmlFields.label(a.path, a.definition.name ?? a.path).toLowerCase()
                const labelB = copy.valXmlFields.label(b.path, b.definition.name ?? b.path).toLowerCase()
                if (labelA === labelB) {
                    return a.path.localeCompare(b.path, 'de')
                }
                return labelA.localeCompare(labelB, 'de')
            })
    }, [copy.valXmlFields])

    const valXmlOptionMap = useMemo<Map<string, ValXmlOption['definition']>>(() => {
        const map = new Map<string, ValXmlOption['definition']>()
        valXmlOptions.forEach(({ path, definition }) => {
            map.set(path, definition)
        })
        return map
    }, [valXmlOptions])

    const handleCloseNewKpiModal = () => setNewKpiDraft(null)

    const handleOpenAddKpi = useCallback(() => {
        setNewKpiDraft({
            value: '',
            valXmlPath: '',
        })
    }, [setNewKpiDraft])

    const handleConfirmNewKpi = () => {
        if (!newKpiDraft) {
            return
        }
        const draft = newKpiDraft
        const trimmedPath = draft.valXmlPath.trim()
        if (!trimmedPath) {
            setSnackbar({ message: copy.validation.missingValXmlField, severity: 'error' })
            return
        }
        const trimmedValue = draft.value.trim()
        if (!trimmedValue) {
            setSnackbar({ message: copy.validation.missingValue, severity: 'error' })
            return
        }
        const valueFormat = getValueFormatForPath(trimmedPath)
        const normalizedValue = normaliseInputValue(trimmedValue, copy.booleanLabels, {
            format: valueFormat,
            locale,
            noGrouping: valueFormat === 'integer' && isYearLikeIntegerPath(trimmedPath),
        })
        const definition = valXmlOptionMap.get(trimmedPath)
        const nameFromSchema =
            definition?.name?.trim() ??
            trimmedPath
                .split('.')
                .pop()
                ?.replace(/([a-z0-9])([A-Z])/g, '$1 $2') ??
            trimmedPath
        const newRow: KpiRow = {
            name: nameFromSchema,
            value: normalizedValue,
            valXmlPath: trimmedPath,
            confidence: MANUAL_ENTRY_CONFIDENCE,
            needsReview: false,
            reviewNotes: undefined,
            reviewDisagreed: false,
            proposedValue: null,
            proposedMetadata: null,
            hasDirectSource: false,
            isCalculated: true,
        }
        onAddRow(newRow)
        setNewKpiDraft(null)
        setSnackbar({ message: copy.validation.kpiAdded, severity: 'success' })
    }

    const handleDraftChange = (updates: Partial<NewKpiDraft>) => {
        setNewKpiDraft((prev) => (prev ? { ...prev, ...updates } : prev))
    }

    const valXmlFilter = useMemo(
        () =>
            createFilterOptions<ValXmlOption>({
                stringify: (option) => {
                    const { definition, path } = option
                    return [
                        copy.valXmlFields.label(path, definition.name ?? ''),
                        copy.valXmlFields.description(path, definition.documentation ?? '') ?? '',
                        definition.name ?? '',
                        path,
                        definition.documentation ?? '',
                        definition.baseType ?? '',
                        definition.typeRef ?? '',
                    ]
                        .filter(Boolean)
                        .join(' ')
                        .toLowerCase()
                },
                matchFrom: 'any',
            }),
        [copy.valXmlFields]
    )

    const schemaDocumentationMap = useMemo(() => {
        const map = new Map<string, string>()
        const registerDoc = (key?: string | null, value?: string | null) => {
            if (!key || !value) {
                return
            }
            const sanitized = normaliseValXmlPathForGrouping(key)
            if (sanitized) {
                map.set(sanitized, value.trim())
            }
        }
        VALXML_ATTRIBUTE_DEFINITIONS.forEach((definition) => {
            const trimmedDoc = definition.documentation?.trim()
            if (!trimmedDoc) {
                return
            }
            registerDoc(definition.path, trimmedDoc)
            registerDoc(`ValXML.${definition.path}`, trimmedDoc)
            registerDoc(`ns1:${definition.path}`, trimmedDoc)
        })
        return map
    }, [])

    // Precompute formatted values with trailing euro for display and copy actions
    const rowsWithMeta = useMemo<DisplayRow[]>(
        () =>
            kpiRows.reduce<DisplayRow[]>((acc, row, index) => {
                const normalizedPath = normaliseValXmlPathForGrouping(row.valXmlPath)
                if (normalizedPath && FORCED_DEFAULT_PATH_SET.has(normalizedPath)) {
                    return acc
                }
                const documentation = normalizedPath ? schemaDocumentationMap.get(normalizedPath) : undefined
                acc.push({
                    ...row,
                    normalizedPath,
                    documentation,
                    originalIndex: index,
                })
                return acc
            }, []),
        [kpiRows, schemaDocumentationMap]
    )

    useEffect(() => {
        const validKeys = new Set(rowsWithMeta.map((row) => getRowEditKey(row)))
        setPendingValueEdits((current) => {
            let mutated = false
            const next = { ...current }
            Object.keys(next).forEach((key) => {
                if (!validKeys.has(key)) {
                    mutated = true
                    delete next[key]
                }
            })
            return mutated ? next : current
        })
    }, [rowsWithMeta, getRowEditKey])

    const { generalSections, rentalSections } = useMemo(() => {
        const generalGroups = new Map<GeneralSectionLabel, DisplayRow[]>()
        const sectionMap = new Map<string, DisplayRow[]>()

        rowsWithMeta.forEach((row) => {
            const normalized = row.normalizedPath
            if (normalized) {
                const match = RENTAL_GROUP_METADATA.find((group) => group.pathSet.has(normalized))
                if (match) {
                    const existing = sectionMap.get(match.label)
                    if (existing) {
                        existing.push(row)
                    } else {
                        sectionMap.set(match.label, [row])
                    }
                    return
                }
            }
            const category = classifyGeneralSection(normalized)
            const store = generalGroups.get(category)
            if (store) {
                store.push(row)
            } else {
                generalGroups.set(category, [row])
            }
        })

        const rentals: RentalSection[] = RENTAL_GROUP_METADATA.map((group) => {
            const rows = sectionMap.get(group.label)
            return rows?.length ? { label: group.label, rows } : null
        }).filter((section): section is RentalSection => Boolean(section))

        const orderedSections: GeneralSection[] = []
        GENERAL_SECTION_ORDER.forEach((label) => {
            const rows = generalGroups.get(label)
            if (rows && rows.length > 0) {
                orderedSections.push({ label, rows })
                generalGroups.delete(label)
            }
        })

        if (generalGroups.size > 0) {
            Array.from(generalGroups.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .forEach(([label, rows]) => {
                    if (rows.length > 0) {
                        orderedSections.push({ label, rows })
                    }
                })
        }

        return { generalSections: orderedSections, rentalSections: rentals }
    }, [rowsWithMeta])

    const usedValXmlPaths = useMemo(() => {
        const set = new Set<string>()
        kpiRows.forEach((row) => {
            if (!row.valXmlPath) {
                return
            }
            const canonical = canonicalizeValXmlPath(row.valXmlPath)
            if (canonical) {
                set.add(canonical.trim().toLowerCase())
            }
        })
        return set
    }, [kpiRows])

    const downloadDisabled = !onDownloadValXml || !canDownloadValXml
    const reviewButtonDisabled = isReviewPending || isPending || isAddModalOpen || !canRunReview || kpiRows.length === 0
    const shouldShowActionButtons = !isPending && kpiRows.length > 0

    const renderRow = (row: DisplayRow, keySuffix: string) => {
        const rowKey = `${row.valXmlPath ?? row.name}-${keySuffix}`
        const confidenceStyle = getConfidenceStyle(row.confidence, copy.labels.manualConfidence)
        const schemaPath = row.normalizedPath && row.normalizedPath.length > 0 ? `ValXML.${row.normalizedPath}` : row.valXmlPath?.trim()
        const rawDocToShow = row.documentation && row.documentation.trim().length > 0 ? row.documentation : undefined
        const docToShow = schemaPath ? copy.valXmlFields.description(schemaPath, rawDocToShow) : rawDocToShow
        const displayName = row.valXmlPath ? copy.valXmlFields.label(row.valXmlPath, row.name) : row.name
        const pageNumber = typeof row.page === 'number' && Number.isFinite(row.page) && row.page > 0 ? row.page : undefined
        const hasInfo = Boolean(row.notes || docToShow || confidenceStyle || pageNumber || schemaPath)
        const proposedValueRaw = typeof row.proposedValue === 'string' ? row.proposedValue : ''
        const trimmedProposedValue = proposedValueRaw.trim()
        const hasProposedValue = trimmedProposedValue.length > 0
        const formattedUnit = formatUnit(row.unit)
        const formattedProposedUnit = formatUnit(row.proposedMetadata?.unit)
        const editKey = getRowEditKey(row)
        const pendingValue = pendingValueEdits[editKey]
        const valueFormat = getValueFormatForPath(row.valXmlPath)
        const valueFormatOptions = {
            format: valueFormat,
            locale,
            noGrouping: valueFormat === 'integer' && isYearLikeIntegerPath(row.valXmlPath),
        }
        const displayValue = formatDisplayValue(row.value, copy.booleanLabels, valueFormatOptions)
        const inputValue = pendingValue !== undefined ? pendingValue : displayValue

        const handleValueChange = (event: ChangeEvent<HTMLInputElement>) => {
            const { value: nextValue } = event.target
            setPendingValueEdits((prev) => {
                if (prev[editKey] === nextValue) {
                    return prev
                }
                return { ...prev, [editKey]: nextValue }
            })
        }

        const handleValueBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (pendingValue === undefined) {
                return
            }
            const { value: rawValue } = event.target
            const normalized = normaliseInputValue(rawValue, copy.booleanLabels, valueFormatOptions)
            const existingValue = kpiRows[row.originalIndex]?.value ?? ''
            if (normalized !== existingValue) {
                onUpdateRow(row.originalIndex, { value: normalized })
            }
            setPendingValueEdits((prev) => {
                if (prev[editKey] === undefined) {
                    return prev
                }
                const next = { ...prev }
                delete next[editKey]
                return next
            })
        }

        const handleAcceptProposedValue = (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation()
            const updates = getAcceptedReviewProposalUpdates(row)
            if (!updates) {
                return
            }
            onUpdateRow(row.originalIndex, updates)
        }

        const handleRejectProposedValue = (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation()
            if (!hasProposedValue) {
                return
            }
            onUpdateRow(row.originalIndex, {
                proposedValue: null,
                proposedMetadata: null,
                reviewDisagreed: false,
                reviewNotes: undefined,
                needsReview: false,
                lastReviewRound: undefined,
            })
        }

        const handleRemoveRow = (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation()
            onRemoveRow(row.originalIndex)
        }

        const showReviewBadge = Boolean(row.reviewDisagreed)
        const notesText = typeof row.notes === 'string' ? row.notes.trim() : ''
        const shouldShowNotes = notesText.length > 0 || row.usedCodeInterpreter

        const reviewNotes = typeof row.reviewNotes === 'string' ? row.reviewNotes.trim() : ''

        const reviewTooltipContent =
            reviewNotes.length > 0 ? (
                <Stack direction="column" gap={tokens.SizingSpacing050}>
                    <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                        {copy.labels.review}
                    </Typography>
                    <InfoBox variant="negative" icon={<WarningIcon />}>
                        <Typography variant="textS" color={tokens.ColorTextBold}>
                            {reviewNotes}
                        </Typography>
                    </InfoBox>
                </Stack>
            ) : null

        const tooltipBaseStyles = {
            backgroundColor: tokens.ColorSurfaceDefault,
            border: `1px solid ${tokens.ColorBorderDefault}`,
            borderRadius: tokens.SizingCornerS,
            boxShadow: '0px 6px 18px rgba(15, 23, 42, 0.12)',
            p: tokens.SizingSpacing150,
            width: '340px',
            color: tokens.ColorTextDefault,
        }

        const tooltipArrowStyles = {
            color: tokens.ColorSurfaceDefault,
            '&:before': {
                border: `1px solid ${tokens.ColorBorderDefault}`,
            },
        }

        const tooltipContent = (
            <Stack direction="column" gap={tokens.SizingSpacing100}>
                {pageNumber ? (
                    <Stack direction="column" gap={tokens.SizingSpacing025}>
                        <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                            {copy.labels.page}
                        </Typography>
                        <ContentChip size="small" label={String(pageNumber)} color="grey" />
                    </Stack>
                ) : null}
                {confidenceStyle ? (
                    <Stack direction="column" gap={tokens.SizingSpacing025}>
                        <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                            {copy.labels.confidence}
                        </Typography>
                        <ContentChip size="small" highContrast label={confidenceStyle.label} color={confidenceStyle.chipColor} />
                    </Stack>
                ) : null}

                {schemaPath ? (
                    <Stack direction="column" gap={tokens.SizingSpacing025}>
                        <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                            {copy.labels.valXmlPath}
                        </Typography>
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {schemaPath}
                        </Typography>
                    </Stack>
                ) : null}
                {docToShow ? (
                    <Stack direction="column" gap={tokens.SizingSpacing025}>
                        <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                            {copy.labels.explanation}
                        </Typography>
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {docToShow}
                        </Typography>
                    </Stack>
                ) : null}
                {shouldShowNotes ? (
                    <Stack direction="column" gap={tokens.SizingSpacing050}>
                        <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                            {copy.labels.notes}
                        </Typography>
                        <InfoBox variant="ordinary" icon={<WarningIcon />}>
                            <Typography variant="textS" color={tokens.ColorTextBold}>
                                {notesText || copy.labels.codeInterpreterUsed}
                                {row.usedCodeInterpreter ? (
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-flex',
                                            verticalAlign: 'text-bottom',
                                            marginLeft: tokens.SizingSpacing050,
                                        }}
                                    >
                                        <CalculatorIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconInformative} />
                                    </Box>
                                ) : null}
                            </Typography>
                        </InfoBox>
                    </Stack>
                ) : null}
            </Stack>
        )

        const labelContent = (
            <Box
                component="div"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    width: '100%',
                    minHeight: tokens.SizingSpacing400,
                    paddingX: tokens.SizingSpacing100,
                    paddingY: tokens.SizingSpacing075,
                    borderRadius: tokens.SizingCornerS,
                }}
            >
                <Typography variant="textS">{displayName}</Typography>
            </Box>
        )

        const nameLabel = hasInfo ? (
            <Tooltip
                title={tooltipContent}
                placement="bottom"
                enterDelay={200}
                leaveDelay={0}
                disableInteractive
                slotProps={{
                    tooltip: {
                        sx: {
                            ...tooltipBaseStyles,
                        },
                    },
                    arrow: {
                        sx: tooltipArrowStyles,
                    },
                }}
            >
                <Box sx={{ display: 'flex', flexGrow: 1, width: '100%' }}>{labelContent}</Box>
            </Tooltip>
        ) : (
            labelContent
        )

        const nameContent = (
            <Stack
                direction="row"
                alignItems="center"
                gap={tokens.SizingSpacing050}
                sx={{ width: '100%', minHeight: tokens.SizingSpacing400 }}
            >
                {nameLabel}
                {showReviewBadge ? (
                    reviewTooltipContent ? (
                        <Tooltip
                            title={reviewTooltipContent}
                            placement="top"
                            enterDelay={200}
                            leaveDelay={0}
                            disableInteractive
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        ...tooltipBaseStyles,
                                    },
                                },
                                arrow: {
                                    sx: tooltipArrowStyles,
                                },
                            }}
                        >
                            <span>
                                <ContentChip size="small" color="red" highContrast label="!!" />
                            </span>
                        </Tooltip>
                    ) : (
                        <ContentChip size="small" color="red" highContrast label="!!" />
                    )
                ) : null}
            </Stack>
        )

        return (
            <TableRow
                key={rowKey}
                hover={Boolean(onRowClick)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{
                    ...getKpiRowStyles({ confidenceStyle, hasClickHandler: Boolean(onRowClick) }),
                    '&:hover .kpi-row-remove': {
                        opacity: 1,
                    },
                }}
            >
                <TableCell
                    sx={{
                        width: tokens.SizingSpacing300,
                        paddingRight: tokens.SizingSpacing050,
                    }}
                >
                    <Box
                        className="kpi-row-remove"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginX: '-12px',
                            opacity: 0,
                            transition: 'opacity 0.15s ease',
                            '&:focus-within': {
                                opacity: 1,
                            },
                        }}
                    >
                        <IconButton
                            size="small"
                            variant="tertiary"
                            icon={<XIcon />}
                            ariaLabel={copy.actions.removeKpi}
                            onClick={handleRemoveRow}
                            disabled={isPending || isReviewPending}
                        />
                    </Box>
                </TableCell>
                <TableCell sx={{ width: '100%', paddingY: 0, paddingRight: tokens.SizingSpacing050 }}>{nameContent}</TableCell>
                <TableCell align="right">
                    <Stack direction="column" alignItems="flex-end" gap={tokens.SizingSpacing050}>
                        <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={tokens.SizingSpacing075}>
                            <TextField
                                value={inputValue}
                                onChange={handleValueChange}
                                onBlur={handleValueBlur}
                                onClick={(event) => event.stopPropagation()}
                                onMouseDown={(event) => event.stopPropagation()}
                                placeholder={copy.placeholders.value}
                                variant="filled"
                                size="small"
                                fullWidth
                                disabled={isPending || isReviewPending}
                                InputProps={{
                                    disableUnderline: true,
                                    startAdornment: formattedUnit ? (
                                        <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                                            {formattedUnit}
                                        </Typography>
                                    ) : undefined,
                                }}
                                sx={{
                                    maxWidth: '100%',
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: tokens.ColorSurfaceDefault,
                                        borderRadius: tokens.SizingCornerS,
                                        paddingTop: tokens.SizingSpacing025,
                                        paddingBottom: tokens.SizingSpacing025,
                                        paddingLeft: tokens.SizingSpacing075,
                                        paddingRight: tokens.SizingSpacing075,
                                        height: tokens.SizingSpacing400,
                                        minWidth: '10rem',
                                        '&.Mui-focused': {
                                            opacity: 1,
                                        },
                                        opacity: 1,
                                        '&:hover': {
                                            backgroundColor: tokens.ColorSurfaceHover,
                                            opacity: 1,
                                        },
                                    },
                                    '& .MuiFilledInput-input': {
                                        padding: 0,
                                        fontSize: '0.875rem',
                                        textAlign: 'right',
                                    },
                                }}
                            />
                            <IconButton
                                size="small"
                                variant="tertiary"
                                tooltip={copy.actions.copyValue}
                                icon={<CopyIcon />}
                                ariaLabel={copy.actions.copyValue}
                                onClick={(event) => {
                                    event.stopPropagation()
                                    void navigator.clipboard.writeText(displayValue)
                                    setSnackbar({ message: copy.feedback.valueCopied, severity: 'success' })
                                }}
                            />
                        </Stack>
                        {hasProposedValue ? (
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="flex-end"
                                sx={{
                                    backgroundColor: tokens.ColorSurfacePositiveInversePressed,
                                    borderRadius: tokens.SizingCornerS,
                                    marginRight: '38px',
                                }}
                            >
                                <Stack direction="row" alignItems="center">
                                    <IconButton
                                        size="small"
                                        variant="tertiary"
                                        tooltip={copy.actions.acceptProposal}
                                        icon={<CheckCircleIcon color={tokens.ColorIconDefault} />}
                                        ariaLabel={copy.actions.acceptProposal}
                                        onClick={handleAcceptProposedValue}
                                        disabled={isPending || isReviewPending}
                                    />
                                    <IconButton
                                        size="small"
                                        variant="tertiary"
                                        tooltip={copy.actions.rejectProposal}
                                        icon={<XCircleIcon color={tokens.ColorIconDefault} />}
                                        ariaLabel={copy.actions.rejectProposal}
                                        onClick={handleRejectProposedValue}
                                        disabled={isPending || isReviewPending}
                                    />
                                </Stack>
                                <TextField
                                    value={formatDisplayValue(trimmedProposedValue, copy.booleanLabels, valueFormatOptions)}
                                    variant="filled"
                                    size="small"
                                    fullWidth
                                    disabled
                                    InputProps={{
                                        disableUnderline: true,
                                        readOnly: true,
                                        startAdornment: formattedProposedUnit ? (
                                            <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                                                {formattedProposedUnit}
                                            </Typography>
                                        ) : undefined,
                                    }}
                                    sx={{
                                        maxWidth: '100px',
                                        '& .MuiFilledInput-root': {
                                            backgroundColor: 'transparent',
                                            paddingTop: tokens.SizingSpacing025,
                                            paddingBottom: tokens.SizingSpacing025,
                                            paddingLeft: tokens.SizingSpacing075,
                                            paddingRight: tokens.SizingSpacing075,
                                            height: tokens.SizingSpacing400,
                                            textAlign: 'right',
                                            '&.Mui-disabled': {
                                                opacity: 1,
                                                color: tokens.ColorTextDefault,
                                            },
                                        },
                                        '& .MuiFilledInput-input': {
                                            padding: 0,
                                            fontSize: '0.875rem',
                                            textAlign: 'right',
                                        },
                                    }}
                                />
                            </Stack>
                        ) : null}
                    </Stack>
                </TableCell>
            </TableRow>
        )
    }

    const renderSectionTable = (rows: DisplayRow[], sectionKey: string) => (
        <Table size="small">
            <TableBody>{rows.map((row, index) => renderRow(row, `${sectionKey}-${index}`))}</TableBody>
        </Table>
    )

    return (
        <>
            <HeaderBar>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                    <Typography variant="headline2">{copy.labels.title}</Typography>
                    <Tooltip
                        title={downloadDisabled ? copy.feedback.valXmlUnavailable : undefined}
                        disableFocusListener={!downloadDisabled}
                        disableTouchListener={!downloadDisabled}
                    >
                        <span>
                            <TextButton
                                label={copy.labels.valXml}
                                icon={<DownloadSimpleIcon />}
                                variant="secondary"
                                disabled={downloadDisabled}
                                onClick={() => {
                                    if (downloadDisabled) {
                                        return
                                    }
                                    onDownloadValXml?.()
                                    setSnackbar({ message: copy.feedback.valXmlExportStarted, severity: 'success' })
                                }}
                            />
                        </span>
                    </Tooltip>
                </Stack>
            </HeaderBar>
            <KpiTableContainer>
                {isPending ? (
                    <Stack
                        direction="column"
                        alignItems="center"
                        sx={{
                            minHeight: kpiRows.length === 0 ? 240 : undefined,
                            paddingX: tokens.SizingSpacing300,
                            paddingY: kpiRows.length > 0 ? tokens.SizingSpacing200 : undefined,
                        }}
                    >
                        <PendingMessage variant="textM" color={tokens.ColorTextDefault}>
                            {kpiRows.length === 0 ? copy.feedback.extractingInitial : copy.feedback.extractingMore}
                        </PendingMessage>
                    </Stack>
                ) : null}
                {kpiRows.length === 0 ? null : (
                    <Stack direction="column" gap={tokens.SizingSpacing200}>
                        {generalSections.length ? (
                            <Stack direction="column" gap={tokens.SizingSpacing100}>
                                {generalSections.map((section, index) => (
                                    <Stack
                                        key={`general-section-${section.label}`}
                                        direction="column"
                                        gap={tokens.SizingSpacing100}
                                        sx={{ mt: index === 0 ? 0 : tokens.SizingSpacing200 }}
                                    >
                                        <Typography variant="headline4">{copy.generalSectionLabel(section.label)}</Typography>
                                        {renderSectionTable(section.rows, `general-${index}`)}
                                    </Stack>
                                ))}
                            </Stack>
                        ) : null}
                        {rentalSections.map((section, index) => (
                            <Stack
                                key={`${section.label}-${index}`}
                                direction="column"
                                gap={tokens.SizingSpacing100}
                                sx={{ mt: index === 0 && generalSections.length === 0 ? 0 : tokens.SizingSpacing200 }}
                            >
                                <Typography variant="headline4">{copy.rentalSectionLabel(section.label)}</Typography>
                                {renderSectionTable(section.rows, `rental-${index}`)}
                            </Stack>
                        ))}
                    </Stack>
                )}
                <Box sx={{ height: tokens.SizingSpacing300 }} />
            </KpiTableContainer>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-end"
                sx={{
                    position: 'sticky',
                    bottom: tokens.SizingSpacing200,
                    marginTop: tokens.SizingSpacing200,
                    padding: `${tokens.SizingSpacing075} ${tokens.SizingSpacing150}`,
                    borderRadius: tokens.SizingCornerS,
                    zIndex: 3,
                }}
            >
                {shouldShowActionButtons ? (
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'inline-flex',
                            alignItems: 'center',
                            bgcolor: tokens.ColorSurfaceDefault,
                        }}
                    >
                        <IconButton
                            size="medium"
                            variant="secondary"
                            icon={<PlusIcon />}
                            ariaLabel={copy.actions.addKpi}
                            tooltip={copy.actions.addMissingKpi}
                            onClick={handleOpenAddKpi}
                            disabled={isPending || isReviewPending}
                        />
                    </Box>
                ) : null}
                {shouldShowActionButtons ? (
                    <Tooltip title={copy.actions.reviewTooltip}>
                        <Box>
                            <TextButton
                                label={isReviewPending ? copy.actions.reviewRunning : copy.actions.review}
                                variant="primary"
                                icon={
                                    isReviewPending ? (
                                        <CircularProgress
                                            size={16}
                                            thickness={3}
                                            sx={{ color: 'currentColor', position: 'relative', top: -1, left: 0 }}
                                        />
                                    ) : (
                                        <SparkleIcon />
                                    )
                                }
                                disabled={reviewButtonDisabled}
                                onClick={() => {
                                    if (!reviewButtonDisabled) {
                                        onRunReview()
                                    }
                                }}
                            />
                        </Box>
                    </Tooltip>
                ) : null}
            </Stack>
            <Snackbar
                open={Boolean(snackbar)}
                onClose={() => setSnackbar(null)}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <div>
                    <Alert severity={snackbar?.severity ?? 'success'} size="small">
                        {snackbar?.message}
                    </Alert>
                </div>
            </Snackbar>
            <AddKpiModal
                isOpen={isAddModalOpen}
                draft={newKpiDraft}
                valXmlOptions={valXmlOptions}
                valXmlFilter={valXmlFilter}
                excludedPaths={usedValXmlPaths}
                onClose={handleCloseNewKpiModal}
                onConfirm={handleConfirmNewKpi}
                onDraftChange={handleDraftChange}
                copy={copy.addModal}
                valXmlFields={copy.valXmlFields}
            />
        </>
    )
}
